<template>
  <div class="notifications">
    <acv-legacy-dropdown
      id="notificationsMenu"
      ref="notificationsMenu"
      align="right"
      @opened="handleOpened"
    >
      <template #trigger="{ toggle }">
        <button
          type="button"
          class="notifications-menu-toggle"
          @click="handleToggleClicked(toggle)"
          @keypress.space="trackNotificationIconClicked"
        >
          <span class="tw-sr-only">
            Notifications Menu
          </span>
          <acv-icon
            icon="notifications_none"
          />
          <acv-badge
            v-if="unseenCount > 0"
            id="unseenBadge"
            class="notification-badge"
            :content="unseenCountDisplay"
          />
        </button>
      </template>
      <div class="dropdown-header">
        <div class="tw-flex tw-flex-wrap">
          <div class="col tw-w-1/2">
            <h6>Notifications</h6>
          </div>
          <div
            v-if="notifications.length"
            class="col tw-w-1/2 tw-text-right"
          >
            <button
              type="button"
              @click="markAllRead"
            >
              Mark All <span class="hidden-xs">
                as Read
              </span>
            </button>
          </div>
        </div>
      </div>
      <div class="items-wrapper">
        <acv-notifications-item
          v-for="notification in notifications"
          :id="notification.id"
          :key="notification.id"
          :body="notification.body"
          :created="notification.created_timestamp"
          :link="getAuctionLink(notification)"
          :thumbnail-url="notification.photo ? notification.photo.acv_thumbnail : ''"
          :title="notification.title"
          :read="notification.read"
          :go-to-auction-on-notification-click="goToAuctionOnNotificationClick"
          @notification-clicked="handleNotificationItemClicked"
        />
      </div>
      <div
        v-if="notifications.length"
        class="view-all-btn"
      >
        <a
          class="dropdown-item"
          role="menuitem"
          tabindex="-1"
          @click.prevent="navigateViewAll"
          @keypress.enter.prevent="navigateViewAll"
        >
          {{ viewAllLink.text }}
        </a>
      </div>
      <div
        v-if="!notifications.length"
        class="no-notifications"
      >
        <span class="dropdown-item disabled">
          You currently have no notifications.
        </span>
      </div>
    </acv-legacy-dropdown>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import eventBus from '@/utilities/EventBus';
import { mapGetters } from 'vuex';
import { AcvBadge, AcvIcon } from '@acv/shared-vuejs';
import links from '@/constants/links';
import Analytics from '@/mixins/Analytics';
import WorkerHelper from '@/workers/WorkerHelper';
import AcvNotificationsItem from '@/components/Notifications/NotificationsItem.vue';
import AcvLegacyDropdown from '@/components/Inputs/AcvLegacyDropdown.vue';

export default defineComponent({
  name: 'AcvNotificationsMenu',
  components: {
    AcvNotificationsItem,
    AcvBadge,
    AcvLegacyDropdown,
    AcvIcon,
  },
  mixins: [
    Analytics,
  ],
  props: {
    icon: {
      type: [Array, String],
      default: 'bell',
    },
    showUnseenCount: {
      type: Boolean,
      default: true,
    },
    goToAuctionOnNotificationClick: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['notificationClicked'],
  data() {
    return {
      links,
      worker: null,
    };
  },
  computed: {
    ...mapGetters([
      'currentUser',
      'unseenCount',
      'isAuthorized',
      'isLimitedUser',
    ]),
    notifications() {
      return this.$store.state.notifications.notifications;
    },
    viewAllLink() {
      return {
        to: '/notifications',
        text: 'View All',
      };
    },
    unseenCountDisplay() {
      return this.showUnseenCount
        ? this.$store.getters.unseenCountDisplay
        : '';
    },
  },
  created() {
    this.worker = new WorkerHelper();
    this.worker.on('setInterval', () => {
      this.loadRecents();
    });

    this.loadRecents();
    if (this.worker) {
      this.worker.trigger('setInterval', { interval: 15000 });
    }
  },
  unmounted() {
    if (this.worker) {
      this.worker.terminate();
    }
  },
  methods: {
    handleNotificationItemClicked({
      $event, auctionId, notificationId, link,
    }) {
      this.$refs.notificationsMenu.hide();
      this.track(this.eventNames.notifications.itemClicked, { auction_id: auctionId });
      this.$store.dispatch('markRead', notificationId);
      this.$store.dispatch('getRecent');
      this.$emit('notificationClicked', { $event, auctionId, notificationId });
      if (link) {
        this.$router.push(link);
      }
    },
    loadRecents() {
      if (this.isAuthorized && !this.isLimitedUser) {
        this.$store.dispatch('getRecent');
      }
    },
    trapMouseOver(e) {
      // the bs component has a mouseover event that auto-scrolls
      // items into place, we don't want that.
      e.preventDefault();
      e.stopPropagation();
    },
    getAuctionLink(notification) {
      return `/auction/${notification.auction_id}`;
    },
    toggleMenu(evt) {
      this.$refs.notificationsMenu.toggle(evt);
    },
    handleOpened() {
      this.dismissUnseen();
    },
    dismissUnseen() {
      // reset the unseen count when the menu is shown
      this.$store.dispatch('markAllSeen');
    },
    markAllRead() {
      if (this.$route.name === 'notifications') {
        // dispatch and let notification list component call api
        eventBus.$emit('markAllRead');
      } else {
        // call api to mark notifications read
        this.$store.dispatch('markAllRead');
        this.track(this.eventNames.notifications.markAllReadClicked);
      }
    },
    trackViewAllClicked() {
      this.track(this.eventNames.notifications.viewAllClicked);
    },
    navigateViewAll() {
      this.trackViewAllClicked();
      this.$router.push(this.viewAllLink.to);
    },
    trackNotificationIconClicked() {
      this.track(this.eventNames.notifications.iconClicked);
    },
    handleToggleClicked(toggle) {
      toggle();
      this.trackNotificationIconClicked();
    },
  },
});
</script>

<style lang="scss" scoped>
.notifications {
  position: relative;

  .notifications-menu-toggle {
    top: -1px;
    background: none;
    border: none;
  }

  .notification-badge {
    z-index: 5;
    padding-top: 2px;
    padding-bottom: 2px;
    right: -10px;
    top: -5px;

    ::v-deep .acv-badge__content {
      font-size: 12px;
    }
  }

  :deep(#notificationsMenu) {
    .acv-legacy-dropdown__panel {
      padding-top: 0;
      padding-bottom: 0;
      margin-top: 18px;
      box-shadow: 0 3px 6px rgb(0 0 0 / 16%), 0 3px 6px rgb(0 0 0 / 23%);
      border: 0 !important;
      transform: translateY(-2px);
      background: white;

      @media screen and (min-width: constants.$sm-min) {
        width: 375px;
      }

      .items-wrapper {
        max-height: 460px;
        overflow-y: auto;
      }

      .view-all-btn {
        a {
          &.dropdown-item {
            line-height: 15px;
            width: 100% !important;
            text-align: center;
            border: none;
            background: color.adjust(#ededed, $blackness: 5%);
            font-size: 13px;
            padding: 12px;
            color: rgb(0 0 0 / 67%);
            border-top: solid 1px rgb(0 0 0 / 10%);
            font-weight: normal;
            outline: none;
            display: block;

            &:hover {
              background: color.adjust(#ededed, $blackness: 10%);
            }

            &:focus {
              outline: none !important;
            }
          }
        }
      }

      .no-notifications {
        cursor: default;
        background: white;
        font-size: 14px;
        width: 375px;
        text-align: center;
        padding-top: 15px;
        height: 100px;

        a {
          cursor: default;
        }

        span {
          cursor: default;
        }
      }
    }
  }
}

.dropdown-header {
  cursor: default;
  background: white;
  border-top: solid 4px constants.$orange;
  text-align: left;
  padding: 5px 15px;
  border-bottom: solid 1px rgb(0 0 0 / 10%);

  .col {
    line-height: 1.2;

    h6 {
      padding-top: 10px;
      font-size: 13px;
      font-weight: bold;
      color: rgb(0 0 0 / 67%);
    }

    button {
      border: none;
      background: none;
      font-size: 13px;
      padding: 10px 0;
      color: rgb(0 0 0 / 40%);
      outline: none;

      &:hover {
        color: constants.$orange;
      }
    }
  }
}
</style>
